import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import textUtils from '../common/utilities/TextUtils';
import {GENERIC_REQUEST_PARAMETER, ADD_UPDATE, getApiPath} from "../../api/ApiConst"
import apiCaller from "../../api/ApiCaller";
import { TABLE_ATTRIBUTE_TYPES } from "../../settings/constants/types";
import { getDataTypes, DataTypeConfigView } from "../../utils/datatypes/DataTypeConfig";




// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};


const RequestParameterAddEdit = (props) => {
    const [parameterTypeKeyValue, setParameterTypeKeyValue] = useState("");
    const [currentPath, setCurrentPath] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [configData, setConfigData] = useState({});
    const [parameterObj, setParameterObj] = useState({
        id: "",
        name: "",
        description: "",
        config: {},
        type: "",
        requestId: "",
        required: false,
        attributes: [],
        listAttribute: false,
    });

    // Load type options from API
    useEffect(() => {
        loadTypeOptions();
    }, []);

    const loadTypeOptions = async () => {
        setIsLoadingTypes(true);
        try {
            const options = await getDataTypes();
            setTypeOptions(options);
        } catch (error) {
            console.error('Error loading type options:', error);
            setTypeOptions([]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    useEffect(() => {
        console.log('RequestParameterAddEdit - props.object:', props.object);
        if (props.object != null) {
            const obj = {
                ...props.object,
                attributes: props.object.attributes || []
            };
            console.log('RequestParameterAddEdit - Setting parameterObj:', obj);
            setParameterObj(obj);
            
            if (props.object.type !== "") {
                setParameterTypeKeyValue({
                    label: props.object.type,
                    value: props.object.type
                });
                // Load existing config data if available
                if (props.object.config) {
                    console.log('Loading config data:', props.object.config);
                    console.log('props.object.config.attributes:', props.object.config.attributes);
                    setConfigData(props.object.config);
                    // For OBJECT types, also load nested attributes from config
                    if (props.object.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                        const attributes = props.object.config.attributes || props.object.attributes || [];
                        console.log('Loading OBJECT attributes from config:', attributes);
                        setParameterObj(prev => ({
                            ...prev,
                            attributes: attributes
                        }));
                        // Ensure configData also has the attributes
                        if (!props.object.config.attributes) {
                            setConfigData(prev => ({
                                ...prev,
                                attributes: attributes
                            }));
                        }
                    }
                } 
            } else {
                setParameterTypeKeyValue("");
                setConfigData({});
            }
        } else {
            // Reset state when no object is provided
            setParameterObj({
                id: "",
                name: "",
                description: "",
                type: "",
                requestId: "",
                required: false,
                attributes: [],
                listAttribute: false,
            });
            setParameterTypeKeyValue("");
            setConfigData({});
        }
        setCurrentPath([]);
    }, [props.object]);

    const toggle = () => {
        props.close();
    }

    //Text Change Event
    const handleTextChange = (e, type) => {
        if (type === "checkbox") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.checked }));
        } else if (type === "text") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.value }));
        } else if (type === "select") {
            setParameterTypeKeyValue(e);
            setParameterObj(prev => ({ ...prev, type: e.value }));
            // Clear config data when type changes, but preserve attributes for OBJECT type
            if (e.value === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                // For OBJECT type, preserve existing attributes in configData
                const existingAttributes = parameterObj.attributes || [];
                console.log('Switching to OBJECT type, preserving attributes:', existingAttributes);
                setConfigData(prev => ({
                    ...prev,
                    attributes: existingAttributes
                }));
            } else {
                setConfigData({});
            }
        }
    }

    const handleConfigChange = useCallback((fieldName, value) => {
        setConfigData(prev => {
            const newConfig = {
                ...prev,
                [fieldName]: value
            };
            console.log('Config changed:', fieldName, value, newConfig);
            return newConfig;
        });
        
        // For OBJECT types, also update parameterObj.attributes when attributes change
        if (parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT && fieldName === 'attributes') {
            setParameterObj(prev => ({
                ...prev,
                attributes: value
            }));
        }
    }, [parameterObj.type]);

    const handleCreateParameter = () => {
        let requestObj = parameterObj;
        if ((textUtils.isEmpty(requestObj.name, "Name", true)) ||
            (textUtils.isEmpty(requestObj.description, "Description", true)) ||
            textUtils.isEmpty(requestObj.type, "Type", true)
        ) {
            return false;
        }

        // Validate attributes if type is OBJECT 
        if ((parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) &&
            (!parameterObj.attributes || parameterObj.attributes.length === 0)) {
            toast.error("Please add at least one attribute for the object type");
            return false;
        }
        console.log("parameterObj", parameterObj);
        // Validate all attribute names and nested objects
        const validateAttributes = (attrs) => {
            if (!Array.isArray(attrs)) return true;
            
            return attrs.every(attr => {
                if (!attr) return true;

                // Check if attribute name is valid
                if (!isValidAttributeName(attr.name)) {
                    toast.error(`Attribute "${attr.name || 'Unnamed'}" must have a valid name`);
                    return false;
                }

                // Check if nested objects have at least one inner attribute
                if ((attr.type === TABLE_ATTRIBUTE_TYPES.OBJECT)) {
                    // Check both config.attributes and direct attributes for nested objects
                    const nestedAttributes = attr.config?.attributes;
                    if (!nestedAttributes || nestedAttributes.length === 0) {
                        toast.error(`Nested object "${attr.name}" must have at least one inner attribute`);
                        return false;
                    }
                    // Recursively validate nested attributes
                    return validateAttributes(nestedAttributes);
                }

                return true;
            });
        };

        // Always validate attributes if they exist, regardless of the main type
        if (parameterObj.attributes && parameterObj.attributes.length > 0) {
            if (!validateAttributes(parameterObj.attributes)) {
                return false;
            }
        }

        // Handle OBJECT type attributes properly
        if (parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
            // For OBJECT types, move attributes to config.attributes
            if (parameterObj.attributes && parameterObj.attributes.length > 0) {
                // Clean nested attributes - remove attributes key from inner elements that don't need it
                const cleanAttributes = (attrs) => {
                    if (!Array.isArray(attrs)) return attrs;
                    return attrs.map(attr => {
                        if (!attr) return attr;
                        const cleanedAttr = { ...attr };
                        if (attr.type !== TABLE_ATTRIBUTE_TYPES.OBJECT) {
                            delete cleanedAttr.attributes;
                            delete cleanedAttr.config;
                        } else {
                            // For nested OBJECT types, move attributes to config.attributes
                            const nestedAttributes = attr.config?.attributes || attr.attributes;
                            if (nestedAttributes && nestedAttributes.length > 0) {
                                if (!cleanedAttr.config) {
                                    cleanedAttr.config = {};
                                }
                                cleanedAttr.config.attributes = cleanAttributes(nestedAttributes);
                            }
                            delete cleanedAttr.attributes;
                        }
                        return cleanedAttr;
                    });
                };
                
                // Initialize config if it doesn't exist
                if (!requestObj.config) {
                    requestObj.config = {};
                }
                const cleanedAttributes = cleanAttributes(parameterObj.attributes);
                console.log('Saving OBJECT attributes to config:', cleanedAttributes);
                requestObj.config.attributes = cleanedAttributes;
            }
            // Remove attributes from root level for OBJECT types
            delete requestObj.attributes;
        } else {
            // Remove attributes key if type is not OBJECT
            delete requestObj.attributes;
        }

        // Add config data to the request object
        if (Object.keys(configData).length > 0) {
            requestObj.config = { ...requestObj.config, ...configData };
        }

        let url = getApiPath(GENERIC_REQUEST_PARAMETER, ADD_UPDATE);
        
        apiCaller.callPostApi(url, requestObj, {}, (response) => {
            toast.success(`Parameter ${parameterObj.id ? "Update" : "Created"} Successfully`);
            props.successCallBack();
            toggle();
        }, (error) => {
             toast.error(error.response.data.message);
        })
    }

    return (
        <>
            <Modal size="lg" show={props.show} onHide={toggle}>
                <Modal.Header closeButton>
                    <Modal.Title>{`${parameterObj?.id ? "Edit" : "Add"} Request Parameter`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Name *</label>
                            <input type="text" className="form-control input-default"
                                id="name"
                                placeholder='Name'
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.name}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Description *</label>
                            <input type="text" className="form-control input-default"
                                id="description"
                                placeholder='Description'
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.description}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Type *</label>
                            <Select
                                options={typeOptions}
                                value={parameterTypeKeyValue}
                                id="type"
                                onChange={(e) => handleTextChange(e, "select")}
                                className="react-select-container"
                                isLoading={isLoadingTypes}
                                placeholder={isLoadingTypes ? "Loading types..." : "Select Type"}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12 text-left">
                            <label className=''>List Attribute</label>
                            <input
                                type="checkbox"
                                name="listAttribute"
                                id="listAttribute"
                                checked={parameterObj.listAttribute}
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                style={{
                                    display: 'block',
                                    width:'max-content',
                                    marginLeft:'5px'
                                }}
                            />
                        </div>
                    </div>
                    
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <div className="row">
                                <div className="col-lg-6">
                                    <label className=''>Required</label>
                                    <input
                                        type="checkbox"
                                        name="required"
                                        id="required"
                                        checked={parameterObj.required}
                                        onChange={(e) => handleTextChange(e, e.target.type)}
                                        style={{
                                            display: 'block',
                                            width:'max-content',
                                            marginLeft:'5px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Data Type Configuration */}
                    {parameterTypeKeyValue && (
                        <>
                            {console.log('Rendering DataTypeConfigView with:', {
                                selectedDataType: parameterTypeKeyValue,
                                configData: configData,
                                configDataAttributes: configData?.attributes,
                                parameterObjId: parameterObj.id,
                                parameterObjAttributes: parameterObj.attributes
                            })}
                            <DataTypeConfigView
                                selectedDataType={parameterTypeKeyValue}
                                configData={configData}
                                onConfigChange={handleConfigChange}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary"
                        onClick={handleCreateParameter}
                    >
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default RequestParameterAddEdit
